import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PERMISSIONS_BY_ROLE,
  roleAtLeast,
  type MeResponse,
  type Permission,
  type UserRole,
} from '@nesc/shared';
import {
  api,
  hasSessionHint,
  setAccessToken,
  setUnauthenticatedHandler,
  type SingleResponse,
} from './api-client';

type AuthState = {
  status: 'loading' | 'authenticated' | 'anonymous';
  me: MeResponse | null;
};

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  /** 画面表示の補助。判定の根拠にはしない（サーバー側で必ず認可する） */
  can: (permission: Permission) => boolean;
  hasRole: (min: UserRole) => boolean;
  user: MeResponse['user'] | null;
  organization: MeResponse['organization'] | null;
  settings: MeResponse['settings'] | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading', me: null });
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    try {
      const response = await api.get<SingleResponse<MeResponse>>('/me');
      setState({ status: 'authenticated', me: response.data });
    } catch {
      setState({ status: 'anonymous', me: null });
    }
  }, []);

  // 起動時にリフレッシュ Cookie からセッションを復元する
  useEffect(() => {
    let canceled = false;
    void (async () => {
      // 一度もログインしていなければ再発行を試みない（無用な 401 を避ける）
      const refreshed = hasSessionHint() ? await api.refresh() : false;
      if (canceled) return;
      if (!refreshed) {
        setState({ status: 'anonymous', me: null });
        return;
      }
      await loadMe();
    })();
    return () => {
      canceled = true;
    };
  }, [loadMe]);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setState({ status: 'anonymous', me: null });
      queryClient.clear();
    });
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<SingleResponse<{ accessToken: string }>>('/auth/login', {
        email,
        password,
      });
      setAccessToken(response.data.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setState({ status: 'anonymous', me: null });
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const role = state.me?.user.role;
    const permissions = new Set(role ? PERMISSIONS_BY_ROLE[role] : []);
    return {
      ...state,
      login,
      logout,
      reload: loadMe,
      can: (permission) => permissions.has(permission),
      hasRole: (min) => (role ? roleAtLeast(role, min) : false),
      user: state.me?.user ?? null,
      organization: state.me?.organization ?? null,
      settings: state.me?.settings ?? null,
    };
  }, [state, login, logout, loadMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('AuthProvider の内側で使用してください');
  return context;
};
